import { Button, Flex, Form, Input, Modal, Table, Tooltip } from "antd";
import DatePicker from "antd/es/date-picker";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { ComposicaoSalvaModel } from "../../models/composicao/composicaoSalvaModel";
import { listarComposicoesSalvas } from "../../services/composicaoService";
import { formatDataHoraLocal } from "../../util/dataHoraAuxiliar";
import { useAppDispatch, useAppSelector } from "../../util/hooks";

interface ModalComposicoesSalvasProps {
  open: boolean;
  onClose: () => void;
  onSelecionar: (item: ComposicaoSalvaModel) => void;
  textoAcao?: string;
}

const ModalComposicoesSalvas = ({ open, onClose, onSelecionar, textoAcao = "Carregar" }: ModalComposicoesSalvasProps) => {
  const composicaoState = useAppSelector((state) => state.composicao);
  const dispatch = useAppDispatch();

  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroPeriodoDe, setFiltroPeriodoDe] = useState<any>(null);
  const [filtroPeriodoAte, setFiltroPeriodoAte] = useState<any>(null);

  useEffect(() => {
    if (open) {
      dispatch(listarComposicoesSalvas());
      setFiltroEmpresa("");
      setFiltroPeriodoDe(null);
      setFiltroPeriodoAte(null);
    }
  }, [open]);

  const composicoesFiltradas = useMemo<ComposicaoSalvaModel[]>(() => {
    return composicaoState.composicoesSalvas.filter((item) => {
      if (filtroEmpresa && !(item.listaEmpresas ?? "").toLowerCase().includes(filtroEmpresa.toLowerCase())) return false;
      //mantém composições cujo período tem interseção com o intervalo filtrado
      if (filtroPeriodoDe && dayjs(item.dataFim).isBefore(filtroPeriodoDe, "day")) return false;
      if (filtroPeriodoAte && dayjs(item.dataInicio).isAfter(filtroPeriodoAte, "day")) return false;
      return true;
    });
  }, [composicaoState.composicoesSalvas, filtroEmpresa, filtroPeriodoDe, filtroPeriodoAte]);

  const colunas: ColumnsType<ComposicaoSalvaModel> = [
    {
      title: "TIPO DE DATA",
      dataIndex: "tipoDataFiltro",
      width: 120,
      render: (value) => (value?.trim() === "SORT" ? "Sorteio" : "Lançamento"),
    },
    {
      title: "PERÍODO",
      dataIndex: "dataInicio",
      width: 190,
      render: (_, record) => `${dayjs(record.dataInicio).format("DD/MM/YYYY")} a ${dayjs(record.dataFim).format("DD/MM/YYYY")}`,
    },
    {
      title: "EMPRESAS",
      dataIndex: "listaEmpresas",
      width: 140,
      render: (value, record) => (
        <Tooltip
          title={
            <ul style={{ paddingLeft: 16, margin: 0 }}>
              {(value ?? "").split(", ").map((nome: string, index: number) => (
                <li key={index}>{nome}</li>
              ))}
            </ul>
          }
        >
          <span style={{ cursor: "pointer", textDecoration: "underline dotted" }}>{`${record.quantidadeEmpresas} empresa(s)`}</span>
        </Tooltip>
      ),
    },
    {
      title: "SALVA EM",
      dataIndex: "dataHoraAlteracao",
      width: 150,
      render: (value) => formatDataHoraLocal(value),
    },
    {
      title: "POR",
      dataIndex: "nomeUsuario",
      width: 160,
    },
    {
      title: "",
      key: "acao",
      width: 110,
      render: (_, record) => (
        <Button className="buttonPrimary" size="small" onClick={() => onSelecionar(record)}>
          {textoAcao}
        </Button>
      ),
    },
  ];

  return (
    <Modal title="Composições Salvas" open={open} onCancel={onClose} footer={null} width={950}>
      <Form layout="vertical">
        <Flex gap={12}>
          <Form.Item label="Empresa">
            <Input.Search
              allowClear
              placeholder=""
              className="searchInput"
              enterButton
              style={{ width: 300 }}
              value={filtroEmpresa}
              onChange={(e) => setFiltroEmpresa(e.target.value)}
              onSearch={(value) => setFiltroEmpresa(value)}
            />
          </Form.Item>
          <Form.Item label="Período de">
            <DatePicker placeholder="" allowClear format="DD/MM/YYYY" style={{ width: 120 }} value={filtroPeriodoDe} onChange={(value) => setFiltroPeriodoDe(value)} />
          </Form.Item>
          <Form.Item label="Período até">
            <DatePicker placeholder="" allowClear format="DD/MM/YYYY" style={{ width: 120 }} value={filtroPeriodoAte} onChange={(value) => setFiltroPeriodoAte(value)} />
          </Form.Item>
        </Flex>
      </Form>
      <Table
        columns={colunas}
        dataSource={composicoesFiltradas}
        loading={composicaoState.loadingComposicoesSalvas}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 8, showSizeChanger: false, showTotal: (total) => `${total} composição(ões)` }}
      />
    </Modal>
  );
};

export default ModalComposicoesSalvas;
